import { useCallback, useMemo, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { notifyOrgPolicyChanged } from "../lib/orgPolicyBroadcast";
import { LockedPolicy, SiteOverride, SiteOverrides, GroupNotificationPreferences } from "../types";
import { errorWithRef, fallbackError } from "../lib/apiError";

/** A tenant group as the console sees it (mirror of the backend TenantGroupResponse, ENG-2454).
 *  `role` is null for policy-only groups (the ones admins create + manage here); a non-null role
 *  marks an ENG-2375 role bucket, whose membership is owned by the member's role, not this UI. */
export interface TenantGroup {
  id: string;
  name: string;
  role: string | null;
  memberCount: number;
  /**
   * ENG-3749: one of a family's starting groups, which can be renamed but not deleted. "kids" only on a
   * family made before ENG-4086, which stopped seeding it.
   */
  familyDefault?: "adults" | "kids" | "extra_protection" | null;
}

/**
 * The wire shape: the backend serializes with `encodeDefaults = false`, so a policy group's null
 * `role` is omitted rather than sent as null — it arrives as `undefined`, which `role === null` and
 * `role !== null` both read as "has a role". That silently classified every policy group as an
 * ENG-2375 role bucket: the Overview group breakdown dropped them (collapsing a tenant with a real
 * Engineering group to a single "Ungrouped" card) and the member detail view refused to remove their
 * chips. `GroupsPage` was unaffected only because it happens to use loose `== null`.
 *
 * Normalized once here, at the boundary, so no consumer has to know the field can be absent and the
 * declared `string | null` type is actually true of the data.
 */
const normalizeGroup = (g: TenantGroup): TenantGroup => ({ ...g, role: g.role ?? null });

/** A group member (mirror of GroupMemberResponse). `tenantUserId` is the id the member endpoints
 *  and the roster (BusinessMember.id) key on. */
export interface GroupMember {
  tenantUserId: string;
  email: string | null;
  role: string | null;
}

/** Group-scoped labs policy (mirror of the backend GroupLabsFeaturesResponse, ENG-2525).
 *  `groupOverrides` = THIS group's explicit on/off (a key absent = inherit); `tenantOverrides` +
 *  `global` are the lower tiers so the dialog can show what a member gets when the group is unset.
 *  This is a per-group editing view — at resolution time the group tier collapses most-restrictively
 *  across a member's groups, so a member can end up stricter than any single group's row here. */
export interface GroupLabsFeatures {
  groupId: string;
  keys: string[];
  groupOverrides: Record<string, boolean>;
  tenantOverrides: Record<string, boolean>;
  global: Record<string, boolean>;
}

/** Group-scoped enforcement (mirror of the backend GroupEnforcementResponse, ENG-2536). `groupMode`
 *  is THIS group's override (null = inherit); `tenantMode` is what it inherits; `monitorLockActive`
 *  blocks setting the group to enforce. The member's effective mode collapses ENFORCE-wins across
 *  their groups server-side — this is a per-group editing view, not that. */
export interface GroupEnforcement {
  groupId: string;
  groupMode: "enforce" | "monitor" | null;
  tenantMode: "enforce" | "monitor";
  monitorLockActive: boolean;
}

/** A group extension allow-list entry (mirror of BusinessExtensionWhitelistResponse, ENG-2536). */
export interface GroupWhitelistEntry {
  id: string;
  extensionName: string;
  chromeExtensionId: string;
  notes?: string | null;
}

/**
 * Read + write client for the tenant-group endpoints shipped in ENG-2454
 * (GET/POST /business/groups, PUT/DELETE /business/groups/{id}, and the nested
 * /business/groups/{id}/members). Mirrors useBusinessTeam: list state + mutations
 * that re-fetch on success. Group-member lists are fetched on demand (per dialog),
 * not held in list state.
 */
export function useBusinessGroups() {
  const [groups, setGroups] = useState<TenantGroup[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  /**
   * Load the tenant's groups. A failure sets `groupsError` and leaves `groupsLoaded` false: the
   * flag means "we have the list", and it also gates the re-fetch in DashboardLayout, so marking a
   * failed load as loaded both rendered a 403/500 as the "No groups yet" empty state and made the
   * failure permanent for the session. Left false, returning to a group-backed page retries.
   */
  const fetchGroups = useCallback(async () => {
    if (!auth.currentUser) return;
    setGroupsLoading(true);
    try {
      const response = await authenticatedFetch("/business/groups");
      if (response.ok) {
        const data = (await response.json()) as TenantGroup[];
        setGroups(Array.isArray(data) ? data.map(normalizeGroup) : []);
        setGroupsError(null);
        setGroupsLoaded(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setGroupsError(errorWithRef(data, () => fallbackError("Couldn't load groups.", response)));
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
      setGroupsError(err instanceof Error ? err.message : "Couldn't load groups.");
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  // Mutation failures are RETURNED to the caller, never written to `groupsError`. Every mutation is
  // raised from a dialog that renders the returned message itself; setting the page-level error too
  // printed it twice (once in the dialog, once in the banner behind it) and left the banner up after
  // the dialog closed. `groupsError` is the load-failure surface only.

  const createGroup = useCallback(async (name: string): Promise<{ success: boolean; error?: string }> => {
    if (!name.trim()) return { success: false, error: "Name is required" };
    try {
      const response = await authenticatedFetch("/business/groups", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      if (response.ok) {
        await fetchGroups();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Failed to create group" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to create group" };
    }
  }, [fetchGroups]);

  const renameGroup = useCallback(async (id: string, name: string): Promise<{ success: boolean; error?: string }> => {
    if (!name.trim()) return { success: false, error: "Name is required" };
    try {
      const response = await authenticatedFetch(`/business/groups/${id}`, {
        method: "PUT",
        body: JSON.stringify({ name: name.trim() }),
      });
      if (response.ok) {
        await fetchGroups();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Failed to rename group" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to rename group" };
    }
  }, [fetchGroups]);

  const deleteGroup = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}`, { method: "DELETE" });
      if (response.ok) {
        await fetchGroups();
        // ENG-3366: its members lose the group's allow list and enforcement, as if each were removed.
        notifyOrgPolicyChanged();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Failed to delete group" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to delete group" };
    }
  }, [fetchGroups]);

  /**
   * Members of a single group — fetched on demand (the manage-members dialog holds these locally).
   *
   * ENG-3350: REJECTS on failure. It used to resolve `[]` for any error, which is a real answer, so
   * the dialog said "No members yet." about a group its own row counted 12 members in.
   */
  const fetchGroupMembers = useCallback(async (id: string): Promise<GroupMember[]> => {
    if (!auth.currentUser) throw new Error("Not signed in");
    const response = await authenticatedFetch(`/business/groups/${id}/members`);
    if (!response.ok) throw new Error(fallbackError("Loading group members failed.", response));
    const data = (await response.json()) as GroupMember[];
    return Array.isArray(data) ? data : [];
  }, []);

  const addGroupMember = useCallback(async (id: string, tenantUserId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/members`, {
        method: "POST",
        body: JSON.stringify({ tenantUserId }),
      });
      if (response.ok) {
        // Refresh member counts, but do not await it (ENG-2605): the write has already landed, and
        // awaiting a second full round trip meant every caller's UI - the chip on the member detail
        // view, the row in the group dialog - sat unchanged for the length of two requests with no
        // spinner, which read as "nothing happened" until a reload. The counts land a moment later.
        void fetchGroups();
        // ENG-3366: a member's groups decide their allow list and their enforcement mode, so this is a
        // policy change like any list write: the extension re-reads, and the Risk Report reloads.
        notifyOrgPolicyChanged();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Failed to add member" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to add member" };
    }
  }, [fetchGroups]);

  const removeGroupMember = useCallback(async (id: string, tenantUserId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/members/${tenantUserId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        void fetchGroups(); // see addGroupMember - not awaited, so the caller can react immediately.
        notifyOrgPolicyChanged(); // ENG-3366: see addGroupMember.
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Failed to remove member" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to remove member" };
    }
  }, [fetchGroups]);

  /** This group's labs policy — fetched on demand (the policy dialog holds it locally). Returns null
   *  on failure so the dialog can show an error rather than a stale/empty grid. */
  const fetchGroupFeatures = useCallback(async (id: string): Promise<GroupLabsFeatures | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/features`);
      if (response.ok) return (await response.json()) as GroupLabsFeatures;
    } catch (err) {
      console.error("Error fetching group features:", err);
    }
    return null;
  }, []);

  /** Set an explicit group override (true or false). A false is a real value — it forces the key OFF
   *  for this group's members, which collapses most-restrictively over a permissive tenant/group. */
  const setGroupFeature = useCallback(async (id: string, featureKey: string, enabled: boolean): Promise<GroupLabsFeatures | null> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/features`, {
        method: "PUT",
        body: JSON.stringify({ featureKey, enabled }),
      });
      if (response.ok) {
        // ENG-3416: a group's flags reach its members through the same plan as its enforcement, so the
        // change is announced the same way (see setGroupEnforcement).
        notifyOrgPolicyChanged();
        return (await response.json()) as GroupLabsFeatures;
      }
    } catch (err) {
      console.error("Error setting group feature:", err);
    }
    return null;
  }, []);

  /** Clear a group override so the key inherits the tenant/global tier again. */
  const clearGroupFeature = useCallback(async (id: string, featureKey: string): Promise<GroupLabsFeatures | null> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/features/${featureKey}`, { method: "DELETE" });
      if (response.ok) {
        notifyOrgPolicyChanged(); // ENG-3416: see setGroupFeature.
        return (await response.json()) as GroupLabsFeatures;
      }
    } catch (err) {
      console.error("Error clearing group feature:", err);
    }
    return null;
  }, []);

  // ── Group notification preferences (ENG-3059) ──
  //
  // Shaped exactly like the three above, with one inverted invariant: the group tier collapses
  // MOST-NOTIFYING across a member's groups (true wins), not most-restrictively. So an explicit
  // `false` here does NOT guarantee that this group's members are unnotified — a member in another
  // group that enabled the category still is. The section says so; do not copy the labs wording.

  /** This group's notification preferences — fetched on demand. Null on failure, so the section can
   *  show an error rather than an empty grid that reads as "nothing configured". */
  const fetchGroupNotificationPreferences = useCallback(async (id: string): Promise<GroupNotificationPreferences | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/notification-preferences`);
      if (response.ok) return (await response.json()) as GroupNotificationPreferences;
    } catch (err) {
      console.error("Error fetching group notification preferences:", err);
    }
    return null;
  }, []);

  const setGroupNotificationPreference = useCallback(
    async (id: string, category: string, enabled: boolean): Promise<GroupNotificationPreferences | null> => {
      try {
        const response = await authenticatedFetch(`/business/groups/${id}/notification-preferences`, {
          method: "PUT",
          body: JSON.stringify({ category, enabled }),
        });
        if (response.ok) return (await response.json()) as GroupNotificationPreferences;
      } catch (err) {
        console.error("Error setting group notification preference:", err);
      }
      return null;
    },
    [],
  );

  /** Clear a group value so the category inherits the tenant tier, then the Haven default. */
  const clearGroupNotificationPreference = useCallback(
    async (id: string, category: string): Promise<GroupNotificationPreferences | null> => {
      try {
        const response = await authenticatedFetch(
          `/business/groups/${id}/notification-preferences/${category}`,
          { method: "DELETE" },
        );
        if (response.ok) return (await response.json()) as GroupNotificationPreferences;
      } catch (err) {
        console.error("Error clearing group notification preference:", err);
      }
      return null;
    },
    [],
  );

  // ── Group enforcement (ENG-2536, slice 2) ──

  const fetchGroupEnforcement = useCallback(async (id: string): Promise<GroupEnforcement | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/enforcement`);
      if (response.ok) return (await response.json()) as GroupEnforcement;
    } catch (err) {
      console.error("Error fetching group enforcement:", err);
    }
    return null;
  }, []);

  /** Force a group to Active (enforce) or Monitor-Only. Returns null on failure (incl. 409 when the
   *  tenant is monitor-locked and enforce was requested — the caller surfaces it). */
  const setGroupEnforcement = useCallback(async (id: string, mode: "enforce" | "monitor"): Promise<GroupEnforcement | null> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/enforcement`, {
        method: "PUT",
        body: JSON.stringify({ mode }),
      });
      if (response.ok) {
        // ENG-3366: the group's members change mode, which the extension and the Risk Report both read.
        notifyOrgPolicyChanged();
        return (await response.json()) as GroupEnforcement;
      }
    } catch (err) {
      console.error("Error setting group enforcement:", err);
    }
    return null;
  }, []);

  /** Clear the group override so enforcement inherits the tenant mode again. */
  const clearGroupEnforcement = useCallback(async (id: string): Promise<GroupEnforcement | null> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/enforcement`, { method: "DELETE" });
      if (response.ok) {
        notifyOrgPolicyChanged(); // ENG-3366: see setGroupEnforcement.
        return (await response.json()) as GroupEnforcement;
      }
    } catch (err) {
      console.error("Error clearing group enforcement:", err);
    }
    return null;
  }, []);

  // ── Group locked/denied protection (ENG-2536, slice 3) ──
  // A group's OWN four locked/denied sets; they UNION with the tenant's for the group's members.

  const fetchGroupLockedPolicy = useCallback(async (id: string): Promise<LockedPolicy | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/locked-policy`);
      if (response.ok) return (await response.json()) as LockedPolicy;
    } catch (err) {
      console.error("Error fetching group locked policy:", err);
    }
    return null;
  }, []);

  /** Replace-all the group's four locked/denied sets. Returns the saved policy, or a failure result. */
  const saveGroupLockedPolicy = useCallback(async (id: string, policy: LockedPolicy): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/locked-policy`, {
        method: "PUT",
        body: JSON.stringify(policy),
      });
      if (response.ok) {
        // ENG-3434: a group's locked and denied sets reach its members through the same plan as its
        // flags, so the save is announced the same way (see setGroupFeature).
        notifyOrgPolicyChanged();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Could not save the group's locked policy" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Could not save the group's locked policy" };
    }
  }, []);

  // ── Group site tier / tracked overrides (ENG-3139) ──
  // A group's OWN overrides; at resolution a group value wins over the tenant's per axis.

  const fetchGroupSiteOverrides = useCallback(async (id: string): Promise<SiteOverrides | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/site-overrides`);
      if (response.ok) return (await response.json()) as SiteOverrides;
    } catch (err) {
      console.error("Error fetching group site overrides:", err);
    }
    return null;
  }, []);

  /** Replace-all the group's site overrides. */
  const saveGroupSiteOverrides = useCallback(async (id: string, overrides: SiteOverride[]): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/site-overrides`, {
        method: "PUT",
        body: JSON.stringify({ overrides }),
      });
      if (response.ok) {
        notifyOrgPolicyChanged(); // ENG-3434: see saveGroupLockedPolicy.
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Could not save the group's site overrides" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Could not save the group's site overrides" };
    }
  }, []);

  // ── Group extension allow-list (ENG-2536, slice 4) ──

  // ENG-3362: null on failure, like the other group reads here. It returned [] for a failed load,
  // which is an empty list: a group whose entries did not load read as having none, and the console
  // then offered to add what was already there.
  const fetchGroupWhitelist = useCallback(async (id: string): Promise<GroupWhitelistEntry[] | null> => {
    if (!auth.currentUser) return null;
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/extension-whitelist`);
      if (response.ok) {
        const data = (await response.json()) as GroupWhitelistEntry[];
        return Array.isArray(data) ? data : null;
      }
    } catch (err) {
      console.error("Error fetching group whitelist:", err);
    }
    return null;
  }, []);

  const addGroupWhitelist = useCallback(async (
    id: string,
    entry: { extensionName: string; chromeExtensionId: string; notes?: string },
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/extension-whitelist`, {
        method: "POST",
        body: JSON.stringify(entry),
      });
      if (response.ok) return { success: true };
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || "Could not add the extension" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Could not add the extension" };
    }
  }, []);

  // ENG-3414: a refusal carries the server's reason and nothing else. The section has its own words for
  // when there is none, which name the extension and say it is still allowed; a generic default here
  // would always win over them.
  const removeGroupWhitelist = useCallback(async (id: string, entryId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/extension-whitelist/${entryId}`, { method: "DELETE" });
      if (response.ok) return { success: true };
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error };
    } catch (err: unknown) {
      console.error("Error removing group allow list entry:", err);
      return { success: false };
    }
  }, []);

  /**
   * ENG-3414: Undo after a group allow-list removal, which was sent at once. Adds the same entry back:
   * its extension id, name and notes. The server gives it a new id. A 409 means the id is on the group's
   * list already, put back by someone else, which is what Undo asked for. Like `removeGroupWhitelist`, a
   * refusal carries only the server's reason.
   */
  const restoreGroupWhitelist = useCallback(async (id: string, entry: GroupWhitelistEntry): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/groups/${id}/extension-whitelist`, {
        method: "POST",
        body: JSON.stringify({ extensionName: entry.extensionName, chromeExtensionId: entry.chromeExtensionId, notes: entry.notes ?? null }),
      });
      if (response.ok || response.status === 409) return { success: true };
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error };
    } catch (err: unknown) {
      console.error("Error restoring group allow list entry:", err);
      return { success: false };
    }
  }, []);

  // Clear all group state on logout / user-switch so it can't leak to the next user.
  const reset = useCallback(() => {
    setGroups([]);
    setGroupsLoaded(false);
    setGroupsLoading(false);
    setGroupsError(null);
  }, []);

  /**
   * The RETURNED OBJECT is memoised too, not just the functions in it (ENG-2722).
   *
   * Consumers take the whole hook — `api: ReturnType<typeof useBusinessGroups>` in the four
   * group-scoped Policy sections — so a fresh object each render is exactly as destabilising as
   * fresh functions were: any effect depending on `api` would fire every render. That is what the
   * suppressions at those call sites were preventing ("the hook objects are recreated each render").
   *
   * With every function stable, this changes only when one of the four state values does. Effects
   * should still depend on the SPECIFIC function they call rather than on `api`, so that a groups
   * refresh does not re-run an unrelated per-group fetch — see GroupExtensionsSection.
   */
  return useMemo(
    () => ({
      groups,
      groupsLoaded,
      groupsLoading,
      groupsError,
      setGroupsError,
      fetchGroups,
      createGroup,
      renameGroup,
      deleteGroup,
      fetchGroupMembers,
      addGroupMember,
      removeGroupMember,
      fetchGroupFeatures,
      setGroupFeature,
      clearGroupFeature,
      fetchGroupNotificationPreferences,
      setGroupNotificationPreference,
      clearGroupNotificationPreference,
      fetchGroupEnforcement,
      setGroupEnforcement,
      clearGroupEnforcement,
      fetchGroupLockedPolicy,
      saveGroupLockedPolicy,
      fetchGroupSiteOverrides,
      saveGroupSiteOverrides,
      fetchGroupWhitelist,
      addGroupWhitelist,
      removeGroupWhitelist,
      restoreGroupWhitelist,
      reset,
    }),
    [groups, groupsLoaded, groupsLoading, groupsError, setGroupsError, fetchGroups, createGroup, renameGroup, deleteGroup, fetchGroupMembers, addGroupMember, removeGroupMember, fetchGroupFeatures, setGroupFeature, clearGroupFeature, fetchGroupNotificationPreferences, setGroupNotificationPreference, clearGroupNotificationPreference, fetchGroupEnforcement, setGroupEnforcement, clearGroupEnforcement, fetchGroupLockedPolicy, saveGroupLockedPolicy, fetchGroupSiteOverrides, saveGroupSiteOverrides, fetchGroupWhitelist, addGroupWhitelist, removeGroupWhitelist, restoreGroupWhitelist, reset],
  );
}
